import { useState, useEffect, useRef } from "react";
import api from "../lib/apiClient";

/**
 * Extracted from frontend/src/hooks/useResidentPhoto.ts (frontend split epic,
 * Phase 2 Task 2.2) — the one export ResidentTable needs; the file's
 * mutation hooks (useUploadResidentPhoto/useDeleteResidentPhoto) stay in
 * frontend, they have no cross-app consumer. Logic unchanged.
 *
 * Returns an authenticated blob URL for a resident photo so it can be used
 * in <img src> / Mantine <Avatar src> without leaking auth headers.
 *
 * - Returns null while loading or when no photo exists.
 * - Handles legacy external URLs (http...) directly without a round-trip.
 * - Revokes the object URL on cleanup to avoid memory leaks.
 */
export function useResidentPhotoUrl(
  residentId: string,
  photoUrl: string | null | undefined,
): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  // Use a ref so the cleanup function can cancel in-flight state updates
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let objectUrl: string | null = null;

    if (!photoUrl) {
      // No photo — resolve asynchronously to avoid synchronous setState in effect
      Promise.resolve().then(() => {
        if (!cancelledRef.current) setBlobUrl(null);
      });
      return () => {
        cancelledRef.current = true;
      };
    }

    if (photoUrl.startsWith("http")) {
      // Legacy external URL — set asynchronously
      Promise.resolve().then(() => {
        if (!cancelledRef.current) setBlobUrl(photoUrl);
      });
      return () => {
        cancelledRef.current = true;
      };
    }

    // S3 key → fetch via authenticated proxy and create a blob URL
    api
      .get(`/residents/${residentId}/photo`, { responseType: "arraybuffer" })
      .then((response) => {
        const contentType =
          (response.headers["content-type"] as string) ?? "image/jpeg";
        const blob = new Blob([response.data as ArrayBuffer], {
          type: contentType,
        });
        objectUrl = URL.createObjectURL(blob);
        if (!cancelledRef.current) setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelledRef.current) setBlobUrl(null);
      });

    return () => {
      cancelledRef.current = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBlobUrl(null);
    };
  }, [residentId, photoUrl]);

  return blobUrl;
}
